export interface HubLink {
  label: string;
  href: string;
  description?: string;
}

export interface HubFact {
  label: string;
  value: string;
}

export interface HubSection {
  heading: string;
  body: string;
  bullets: string[];
}

export interface HubFaq {
  question: string;
  answer: string;
}

export interface TopicHub {
  slug: string;
  path: string;
  title: string;
  description: string;
  h1: string;
  eyebrow: string;
  answer: string;
  lastUpdated: string;
  image: string;
  facts: HubFact[];
  primaryLinks: HubLink[];
  sections: HubSection[];
  faqs: HubFaq[];
  relatedLinks: HubLink[];
  sources: HubLink[];
}

export const topicHubs: TopicHub[] = [
  {
    "slug": "lagos-airport",
    "path": "/lagos-airport",
    "title": "Lagos Airport: Choose Your Terminal and Plan Your Journey | airports.ng",
    "description": "Find the right Lagos airport guide for terminal identification, international arrivals, departures and connections between international and domestic flights.",
    "h1": "Lagos Airport: Choose Your Terminal and Plan Your Journey",
    "eyebrow": "LOS airport planning",
    "answer": "Lagos airport uses the IATA code LOS, but the code does not identify your departure terminal. Your first task is to match the terminal on your airline confirmation to the right journey: an international arrival, a departure or a connection involving a road transfer.",
    "lastUpdated": "2026-10-23",
    "image": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80",
    "facts": [
      {
        "label": "Airport code",
        "value": "LOS"
      },
      {
        "label": "First check",
        "value": "Airline and terminal"
      },
      {
        "label": "Connection detail",
        "value": "One booking or separate tickets?"
      },
      {
        "label": "Pickup detail",
        "value": "Terminal and meeting point"
      }
    ],
    "primaryLinks": [
      {
        "label": "Understand Lagos terminal names",
        "href": "/blog/los-airport-guide-2026",
        "description": "Distinguish MMIA, MMA2 and the domestic terminal before arranging transport."
      },
      {
        "label": "Arriving on an international flight",
        "href": "/blog/comprehensive-lagos-airport-guide-2026",
        "description": "Prepare documents, baggage collection, connectivity and the meeting with your driver."
      },
      {
        "label": "Connecting to a domestic flight",
        "href": "/blog/lagos-airport-los-terminal-transfer-guide-2026",
        "description": "Check baggage collection, road movement and the next check-in deadline."
      }
    ],
    "sections": [
      {
        "heading": "Before you arrange a car",
        "body": "Send your driver the airline, flight number, date and confirmed terminal. Saying only “Lagos airport” leaves room for an avoidable collection mistake. Agree how you will reconnect if you have no mobile service.",
        "bullets": [
          "Save the collection instructions offline.",
          "Identify the meeting point after you are free to leave arrivals.",
          "For a departure, allow for reaching the actual terminal and completing the airline’s formalities."
        ]
      },
      {
        "heading": "Before you buy a connection",
        "body": "A transfer between international and domestic facilities involves more than scheduled flight time. Check whether bags must be collected, whether you must enter Nigeria and whether the second ticket is protected if the first flight is late.",
        "bullets": [
          "Ask the airline what happens if the connection is missed.",
          "Check entry requirements before assuming you can remain airside.",
          "Use the transfer guide to list the steps for your exact booking."
        ]
      }
    ],
    "faqs": [
      {
        "question": "Does LOS identify MMA2?",
        "answer": "No. LOS identifies Lagos airport for booking purposes; the airline and flight determine the terminal. Confirm the terminal on your booking."
      },
      {
        "question": "Which guide should I use for departure?",
        "answer": "Use the Lagos departure guide for check-in, screening and boarding preparation. The arrival and connection guides address different journeys."
      },
      {
        "question": "Where do I confirm a changed terminal?",
        "answer": "Check the airline’s latest message and booking information, then ask the airline or airport information desk if unclear."
      }
    ],
    "relatedLinks": [
      {
        "label": "Lagos airport directory record",
        "href": "/airport/lagos-murtala-muhammed"
      },
      {
        "label": "Departing Lagos",
        "href": "/blog/navigating-lagos-airport-los-guide"
      },
      {
        "label": "Cash and mobile data after landing",
        "href": "/blog/currency-exchange-nigerian-airports-2026"
      }
    ],
    "sources": [
      {
        "label": "FAAN passenger information desks",
        "href": "https://faan.gov.ng/passenger-experience/"
      },
      {
        "label": "MMA2 flight and airline guidance",
        "href": "https://mma2.ng/flight-and-airline/"
      }
    ]
  },
  {
    "slug": "abuja-airport",
    "path": "/abuja-airport",
    "title": "Abuja Airport: Terminals, Help and City Transfers | airports.ng",
    "description": "Plan an Abuja airport arrival or departure with terminal guidance, transfer questions, assistance arrangements and Lagos–Abuja journey planning.",
    "h1": "Abuja Airport: Terminals, Help and City Transfers",
    "eyebrow": "ABV airport planning",
    "answer": "Abuja Nnamdi Azikiwe International Airport uses the code ABV. Build your plan around the confirmed terminal, your arrival or check-in time and your onward address. The guides below separate airport preparation from choosing a flight and handling a disruption.",
    "lastUpdated": "2026-10-23",
    "image": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80",
    "facts": [
      {
        "label": "Airport code",
        "value": "ABV"
      },
      {
        "label": "City",
        "value": "Abuja"
      },
      {
        "label": "Confirm with airline",
        "value": "Terminal and check-in deadline"
      },
      {
        "label": "Arrange ahead",
        "value": "Transport and assistance"
      }
    ],
    "primaryLinks": [
      {
        "label": "Abuja airport planning guide",
        "href": "/blog/abuja-airport-abv-terminals-services",
        "description": "Terminal, assistance and transfer checks before the journey."
      },
      {
        "label": "Lagos to Abuja journey guide",
        "href": "/blog/lagos-to-abuja-flight-guide-2026",
        "description": "Compare fares alongside baggage, flexibility and airport access."
      },
      {
        "label": "Abuja airport directory record",
        "href": "/airport/abuja-nnamdi-azikiwe",
        "description": "Airport identity and links to the responsible operators."
      }
    ],
    "sections": [
      {
        "heading": "Plan the full arrival",
        "body": "An arrival time is not the time you will reach an Abuja address. Include document checks where relevant, baggage collection and the agreed ground transfer. Confirm a fare and destination with your transport provider rather than relying on an old airport-price list.",
        "bullets": [
          "Share the terminal with the person collecting you.",
          "Keep your destination and contact number available offline.",
          "If a flight changes, update the driver before leaving the terminal."
        ]
      },
      {
        "heading": "Arrange help before travel",
        "body": "Tell your airline about mobility assistance or equipment when booking. Ask where you should report and how assistance continues through boarding, arrival and any onward transfer.",
        "bullets": [
          "Get confirmation for each flight on the itinerary.",
          "Keep equipment dimensions and battery information available if relevant.",
          "Use an airline or airport information desk for help with a live operational issue."
        ]
      }
    ],
    "faqs": [
      {
        "question": "What is Abuja airport’s code?",
        "answer": "ABV is the IATA code used for Abuja Nnamdi Azikiwe International Airport."
      },
      {
        "question": "Can I assume a service is available in every terminal?",
        "answer": "No. Confirm the terminal, location and opening hours of any service essential to your journey with its operator."
      },
      {
        "question": "What should I do if my Abuja flight is disrupted?",
        "answer": "Ask the airline about assistance and journey options, save the updates and use the passenger-rights guide to prepare any complaint."
      }
    ],
    "relatedLinks": [
      {
        "label": "Accessible travel planning",
        "href": "/blog/accessible-travel-nigeria-disability-guide"
      },
      {
        "label": "Passenger rights",
        "href": "/passenger-rights"
      },
      {
        "label": "Domestic flight planning",
        "href": "/domestic-flights-nigeria"
      }
    ],
    "sources": [
      {
        "label": "FAAN passenger information desks",
        "href": "https://faan.gov.ng/passenger-experience/"
      },
      {
        "label": "NCAA consumer-protection regulations",
        "href": "https://ncaa.gov.ng/media/co1nbi24/nig-cars-part-19-consumer-protection.pdf"
      }
    ]
  },
  {
    "slug": "domestic-flights-nigeria",
    "path": "/domestic-flights-nigeria",
    "title": "Domestic Flights in Nigeria: Compare the Whole Trip | airports.ng",
    "description": "Plan a Nigerian domestic flight by checking the actual route, total fare, baggage, terminal, flexibility and disruption arrangements.",
    "h1": "Domestic Flights in Nigeria: Compare the Whole Trip",
    "eyebrow": "Routes, bookings and preparation",
    "answer": "Start with flights actually offered for your date and the airports you can reach. Then compare total cost, baggage and the consequences of changing or missing a flight. A general airline guide is not a live route map, safety ranking or quotation.",
    "lastUpdated": "2026-10-23",
    "image": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80",
    "facts": [
      {
        "label": "First comparison",
        "value": "Route and travel date"
      },
      {
        "label": "Total cost",
        "value": "Fare, baggage and transfers"
      },
      {
        "label": "Key document",
        "value": "Booking confirmation"
      },
      {
        "label": "Before packing",
        "value": "Carrier-specific rules"
      }
    ],
    "primaryLinks": [
      {
        "label": "Plan a domestic route",
        "href": "/blog/domestic-flights-nigeria-airlines-routes",
        "description": "Build a shortlist using live airline schedules and the right airport codes."
      },
      {
        "label": "Assess safety and reliability claims",
        "href": "/blog/safest-domestic-airlines-2026",
        "description": "Understand what regulatory status and anecdotal experiences can establish."
      },
      {
        "label": "Compare prices without booking myths",
        "href": "/blog/how-to-book-cheap-flights-nigeria-secrets",
        "description": "Check the final price and fare conditions instead of relying on a “cheapest day” rule."
      }
    ],
    "sections": [
      {
        "heading": "Compare like for like",
        "body": "A lower displayed fare may be less useful if it excludes the bag you need or leaves no workable alternative after a change. Make a short comparison using the same date, passenger names and luggage requirements.",
        "bullets": [
          "Read the included checked and cabin allowance for the exact fare.",
          "Record change, cancellation and no-show conditions.",
          "Include airport transport and any overnight stay in the budget."
        ]
      },
      {
        "heading": "Prepare for the airport process",
        "body": "Check the airline’s identification requirements, check-in deadline and terminal before leaving home. Confirm unusual baggage or assistance in advance. An estimated flight duration does not tell you when check-in closes.",
        "bullets": [
          "Save the booking reference and airline contact channel offline.",
          "Use the restricted-items guide before packing batteries, liquids or tools.",
          "For a problem, keep the airline messages and record the outcome you are requesting."
        ]
      }
    ],
    "faqs": [
      {
        "question": "Does an airline guide show every current route?",
        "answer": "No. Confirm the route and operating date in the airline’s own booking system before making plans."
      },
      {
        "question": "Is the cheapest displayed fare the cheapest trip?",
        "answer": "Not necessarily. Compare baggage, fare flexibility and ground transport as well as the ticket price."
      },
      {
        "question": "Are baggage allowances the same for all domestic airlines?",
        "answer": "No. The carrier, fare and itinerary can change the allowance. Check the booking and current airline policy."
      }
    ],
    "relatedLinks": [
      {
        "label": "Nigerian airline baggage",
        "href": "/blog/nigerian-airlines-baggage-allowance-fees-2026"
      },
      {
        "label": "Restricted items",
        "href": "/blog/prohibited-items-domestic-flights-nigeria"
      },
      {
        "label": "Passenger rights",
        "href": "/passenger-rights"
      }
    ],
    "sources": [
      {
        "label": "Air Peace baggage policy",
        "href": "https://flyairpeace.com/baggage-policy/"
      },
      {
        "label": "Ibom Air baggage policy",
        "href": "https://www.ibomair.com/travel-information/baggage/"
      },
      {
        "label": "NCAA consumer-protection regulations",
        "href": "https://ncaa.gov.ng/media/co1nbi24/nig-cars-part-19-consumer-protection.pdf"
      }
    ]
  },
  {
    "slug": "nigeria-visa-immigration",
    "path": "/nigeria-visa-immigration",
    "title": "Nigeria Entry Planning: Visas, Passports and Landing Cards | airports.ng",
    "description": "Separate Nigeria visa eligibility, passport validity, landing cards and health documentation before booking or boarding.",
    "h1": "Nigeria Entry Planning: Visas, Passports and Landing Cards",
    "eyebrow": "Documents before departure",
    "answer": "Plan entry documents before departure. Nigeria’s former Visa on Arrival process is no longer available. Start with Nigeria Immigration Service visa categories for your nationality and purpose, then check your passport, airline boarding requirements and any landing-card obligations separately.",
    "lastUpdated": "2026-10-23",
    "image": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80",
    "facts": [
      {
        "label": "Visa authority",
        "value": "Nigeria Immigration Service"
      },
      {
        "label": "Former Visa on Arrival",
        "value": "Do not rely on it"
      },
      {
        "label": "Landing card",
        "value": "Separate from a visa"
      },
      {
        "label": "Document check",
        "value": "Each traveller individually"
      }
    ],
    "primaryLinks": [
      {
        "label": "Choose the right visa application",
        "href": "/blog/nigeria-visa-requirements-explained",
        "description": "Check purpose, nationality, documents and application route."
      },
      {
        "label": "What replaced Visa on Arrival",
        "href": "/blog/visa-on-arrival-nigeria-2026",
        "description": "Correct an outdated travel plan before you book or board."
      },
      {
        "label": "Landing and exit cards",
        "href": "/blog/nigeria-landing-exit-card-guide",
        "description": "Who needs them, the Nigerian-passport exemption and common form problems."
      }
    ],
    "sections": [
      {
        "heading": "Resolve eligibility before paying",
        "body": "A visitor category is not permission for every activity. Describe why you are travelling and check the category, permitted stay and conditions on the official service. A hotel booking or an invitation alone does not prove entry eligibility.",
        "bullets": [
          "Use the application link provided by Nigeria Immigration Service.",
          "Check transit-country requirements as well as Nigerian entry rules.",
          "Keep the approval and any supporting documents accessible during travel."
        ]
      },
      {
        "heading": "Keep the checks separate",
        "body": "A passport, visa, landing card and health certificate serve different purposes. Completing one does not automatically complete the others. The airline may need evidence before boarding even when you expect to resolve something on arrival.",
        "bullets": [
          "Check each family member’s nationality and travel document.",
          "Discuss an expired or lost passport with the Nigerian mission and airline before departure.",
          "Consult qualified travel-health advice for your itinerary and circumstances."
        ]
      }
    ],
    "faqs": [
      {
        "question": "Can I use the former Visa on Arrival procedure?",
        "answer": "Do not rely on it. Current UK government entry guidance states that Visa on Arrival is no longer available; use Nigeria Immigration Service’s current application categories."
      },
      {
        "question": "Is a landing card a visa?",
        "answer": "No. It is a separate travel declaration. The official landing-card FAQ explains eligibility and exemptions, including Nigerian passport holders."
      },
      {
        "question": "Where should I verify my own eligibility?",
        "answer": "Use Nigeria Immigration Service or the relevant Nigerian mission, and confirm document acceptance with your airline before travel."
      }
    ],
    "relatedLinks": [
      {
        "label": "Expired Nigerian passport planning",
        "href": "/blog/travelling-to-nigeria-with-expired-passport-2026"
      },
      {
        "label": "Yellow Card preparation",
        "href": "/blog/nigeria-e-yellow-card-online-guide-2026"
      },
      {
        "label": "International arrival at Lagos",
        "href": "/blog/comprehensive-lagos-airport-guide-2026"
      }
    ],
    "sources": [
      {
        "label": "Nigeria Immigration Service visa categories",
        "href": "https://immigration.gov.ng/nigerian-visa/"
      },
      {
        "label": "Official landing-card FAQ",
        "href": "https://lecard.immigration.gov.ng/faq"
      },
      {
        "label": "UK government: Nigeria entry requirements",
        "href": "https://www.gov.uk/foreign-travel-advice/nigeria/entry-requirements"
      }
    ]
  },
  {
    "slug": "airport-codes",
    "path": "/airport-codes",
    "title": "Nigerian Airport Codes: Check Your Destination | airports.ng",
    "description": "Look up airport codes, distinguish a destination from a terminal and cross-check airport names before booking or arranging a transfer.",
    "h1": "Nigerian Airport Codes: Check Your Destination",
    "eyebrow": "Codes and airport identity",
    "answer": "A three-letter IATA code helps identify the airport in a booking. Use it alongside the full airport name and city, then confirm the terminal separately. LOS identifies Lagos airport and ABV identifies Abuja; neither code alone tells a driver which terminal to use.",
    "lastUpdated": "2026-10-23",
    "image": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80",
    "facts": [
      {
        "label": "Lagos",
        "value": "LOS"
      },
      {
        "label": "Abuja",
        "value": "ABV"
      },
      {
        "label": "Port Harcourt",
        "value": "PHC"
      },
      {
        "label": "Kano",
        "value": "KAN"
      }
    ],
    "primaryLinks": [
      {
        "label": "Search the airport directory",
        "href": "/airports",
        "description": "Look up an airport by name, city or IATA code."
      },
      {
        "label": "Read the airport-code explainer",
        "href": "/blog/understanding-nigerian-airport-codes",
        "description": "Understand IATA codes, operational identifiers and booking checks."
      },
      {
        "label": "Practise airport codes",
        "href": "/quiz",
        "description": "Use the code quiz as a learning activity."
      }
    ],
    "sections": [
      {
        "heading": "Before you confirm a ticket",
        "body": "Read the full itinerary, including every arrival and departure airport. A city name can be less precise than the airport details, especially when comparing separate bookings.",
        "bullets": [
          "Match the code and full airport name to the intended destination.",
          "For a connection, check whether the next flight uses a different airport.",
          "Confirm the terminal with the airline once the flight is selected."
        ]
      },
      {
        "heading": "When a code does not settle the question",
        "body": "An airport code establishes identity, not today’s schedule or service availability. Do not infer that a route operates, that a lounge is accessible or that a transfer stays airside merely because two tickets share an airport code.",
        "bullets": [
          "Use the airline for current route and terminal information.",
          "Use the operator for airport-specific service questions.",
          "Check unfamiliar codes with IATA’s official search."
        ]
      }
    ],
    "faqs": [
      {
        "question": "What is the airport code for Lagos?",
        "answer": "LOS is the IATA code for Lagos Murtala Muhammed Airport."
      },
      {
        "question": "What is the airport code for Abuja?",
        "answer": "ABV is the IATA code for Abuja Nnamdi Azikiwe International Airport."
      },
      {
        "question": "Does an airport code identify a terminal?",
        "answer": "No. Confirm the terminal for the particular airline and flight; multiple terminals can share an airport code."
      }
    ],
    "relatedLinks": [
      {
        "label": "Lagos airport record",
        "href": "/airport/lagos-murtala-muhammed"
      },
      {
        "label": "Abuja airport record",
        "href": "/airport/abuja-nnamdi-azikiwe"
      },
      {
        "label": "Port Harcourt airport record",
        "href": "/airport/port-harcourt-international"
      }
    ],
    "sources": [
      {
        "label": "IATA airline and airport code search",
        "href": "https://www.iata.org/codes/"
      },
      {
        "label": "FAAN passenger information",
        "href": "https://faan.gov.ng/passenger-experience/"
      }
    ]
  },
  {
    "slug": "airport-services",
    "path": "/airport-services",
    "title": "Airport Services in Nigeria: Arrange What Your Journey Needs | airports.ng",
    "description": "Prepare airport assistance, lounge access, transport, cash, connectivity and baggage support using service-specific guides and official contacts.",
    "h1": "Airport Services in Nigeria: Arrange What Your Journey Needs",
    "eyebrow": "Services to confirm in advance",
    "answer": "Services vary by terminal and provider. Identify what your journey depends on, confirm the terms before paying and keep a backup for anything that may be unavailable on arrival. These guides help you ask specific questions without assuming every airport offers the same facilities.",
    "lastUpdated": "2026-10-23",
    "image": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80",
    "facts": [
      {
        "label": "Lounge access",
        "value": "Terminal, eligibility and hours"
      },
      {
        "label": "Transfer",
        "value": "Vehicle, meeting point and price"
      },
      {
        "label": "Assistance",
        "value": "Arrange with the airline"
      },
      {
        "label": "Baggage problem",
        "value": "Report and retain a reference"
      }
    ],
    "primaryLinks": [
      {
        "label": "Check lounge access",
        "href": "/blog/best-airport-lounges-nigeria-business-travel",
        "description": "Confirm the terminal, membership or fare conditions, guests and hours."
      },
      {
        "label": "Prepare cash and mobile data",
        "href": "/blog/currency-exchange-nigerian-airports-2026",
        "description": "Plan payment and connectivity before meeting your driver."
      },
      {
        "label": "Handle missing or damaged baggage",
        "href": "/blog/lost-luggage-what-to-do-nigerian-airports",
        "description": "Keep tracing, claims evidence and deadlines organized."
      }
    ],
    "sections": [
      {
        "heading": "Make a useful service enquiry",
        "body": "Tell the provider the date, terminal, party size and relevant equipment or luggage. Ask for the total price, what it includes and what happens if the flight changes. Save the reply with your booking.",
        "bullets": [
          "For a car, confirm the driver, vehicle and meeting procedure.",
          "For a lounge, check airside access and the guest or child policy.",
          "For mobility assistance, obtain airline confirmation for each flight."
        ]
      },
      {
        "heading": "Prepare for a service being unavailable",
        "body": "A late flight, closed desk or unusable phone can affect the rest of your arrival. Choose a backup that does not depend on the same service.",
        "bullets": [
          "Save your destination and transport contact offline.",
          "Keep an alternative payment option within your own bank’s rules.",
          "Ask an official information desk for directions instead of following an unsolicited offer."
        ]
      }
    ],
    "faqs": [
      {
        "question": "Can I rely on a lounge listed for the same airport?",
        "answer": "Check its terminal, security area, opening hours and access conditions. A listing at the airport does not establish access from your flight."
      },
      {
        "question": "When should I request mobility assistance?",
        "answer": "Contact the airline when booking or as early as possible, explain the assistance needed and confirm its notice requirements."
      },
      {
        "question": "Who handles a missing checked bag?",
        "answer": "Start with the airline or ground handler and keep a written report reference. Ask about any separate claim process."
      }
    ],
    "relatedLinks": [
      {
        "label": "Accessible travel planning",
        "href": "/blog/accessible-travel-nigeria-disability-guide"
      },
      {
        "label": "Airport cars and drivers",
        "href": "/blog/renting-car-nigerian-airports-guide"
      },
      {
        "label": "Airport directory",
        "href": "/airports"
      }
    ],
    "sources": [
      {
        "label": "FAAN passenger information desks",
        "href": "https://faan.gov.ng/passenger-experience/"
      },
      {
        "label": "MMA2 help and contacts",
        "href": "https://mma2.ng/help-contacts/"
      },
      {
        "label": "Ibom Air baggage and assistance information",
        "href": "https://www.ibomair.com/travel-information/baggage/"
      }
    ]
  }
];

export const getTopicHubByPath = (path: string) => {
  const pathname = path.split('?')[0].replace(/\/$/, '') || '/';
  return topicHubs.find(hub => hub.path === pathname);
};

export const getTopicHubBySlug = (slug: string) => topicHubs.find(hub => hub.slug === slug);
